/**
 * Title and description length policy.
 *
 * Kept in its own module (rather than in `seo.tsx`) so it can be imported
 * without pulling in a component file — `seo.tsx` exports a React component,
 * and the react-refresh lint forbids a component file from also exporting
 * plain functions.
 *
 * These numbers mirror `THRESHOLDS` in scripts/audit-deployed-seo.mts
 * (titleLenRange [20,80], descriptionLenRange [50,180]). Keep the two in
 * step: the policy is about what a search engine actually displays, so
 * anything past these limits is discarded by the SERP anyway.
 */

export const TITLE_MAX = 80
export const DESCRIPTION_MAX = 180

/**
 * Collapse a label that may carry line breaks into a single line.
 *
 * The enquiry label is two lines on screen ("Enquiry" / "and Pricing"), but
 * JSON-LD names, <title> and meta tags have no notion of a line break — a
 * newline there ends up in the structured data verbatim.
 */
export function oneLine(text: string): string {
  return text.replace(/\s*\n+\s*/g, ' ').trim()
}

/**
 * Trim `text` to at most `max` characters, on a word boundary, with an
 * ellipsis.
 *
 * The cut prefers a word boundary and only falls back to a hard cut when
 * there is no space near the end. A trailing punctuation run is removed
 * before the ellipsis so the result does not read "…word, …".
 */
export function trimToLength(text: string, max: number): string {
  if (text.length <= max) return text
  const room = max - 1 // room for the ellipsis
  const cut = text.slice(0, room)
  const lastSpace = cut.lastIndexOf(' ')
  const body = (lastSpace > room * 0.5 ? cut.slice(0, lastSpace) : cut).replace(
    /[\s,;:.、，。；：·—–-]+$/,
    '',
  )
  return `${body}…`
}
